/**
 * SettingsPage.js
 * ===============
 *
 * The unified Settings shell — every account-level surface lives here
 * behind ONE grouped left rail, in the same visual language as the
 * Goals detail page (borderless rail with the 4px right-edge active
 * indicator, outlined content panel, goal-style page header):
 *
 *   ACCOUNT        Profile · Security · Preferences · Notifications · API Keys
 *   BILLING        Overview · Usage & Limits · Activity · Invoices
 *   MONETIZATION   Dashboard · Published Signals · Earnings
 *   ORGANIZATION   Organization (org-tier only) · Organization Members
 *
 * URL contract (deep-linkable):
 *   /settings                        -> /settings/profile
 *   /settings/<section>              -> e.g. /settings/api-keys
 *   /settings/billing/<sub>          -> e.g. /settings/billing/usage
 *   /settings/monetization/<sub>     -> e.g. /settings/monetization/payouts
 *
 * There is deliberately no page header — the breadcrumbs
 * (``Settings › <Group> › <Section>``) carry the context, and the
 * billing group's plan / renewal / balance info lives on the
 * BalanceStrip inside the panel.
 *
 * Old standalone routes (/billing, /api-tokens, /monetization,
 * /credits, legacy /settings/<tab> keys) redirect here from App.js so
 * bookmarks keep working.
 */

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Divider, List, ListItemButton, ListItemIcon, ListItemText,
  Paper, Typography, alpha,
} from '@mui/material';
import {
  Person as PersonIcon,
  GppGood as GppGoodIcon,
  Tune as TuneIcon,
  NotificationsNone as NotificationsIcon,
  Key as KeyIcon,
  Group as GroupIcon,
  CorporateFare as CorporateFareIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  ShowChart as ShowChartIcon,
  History as HistoryIcon,
  Receipt as ReceiptIcon,
  Storefront as StorefrontIcon,
  Paid as PaidIcon,
} from '@mui/icons-material';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import NotificationsTab from './NotificationsTab';
import ProfileTabPlaceholder from './ProfileTabPlaceholder';
import SecurityTab from './SecurityTab';
import PreferencesTab from './PreferencesTab';
import OrganizationMembersTab from './OrganizationMembersTab';
import OrganizationSettingsTab from './OrganizationSettingsTab';
import APITokensPage from '../auth/APITokensPage';
import MonetizationPage from '../marketplace/MonetizationPage';
import BillingSection from '../billing/BillingPage';
import { fetchRequest as fetchBillingBalance } from '../../redux/actions/billing/balance';
import { fetchRequest as fetchOrganizationMine } from '../../redux/actions/settings/organizationMine';
import { useViewerOrganizations } from '../marketplace/marketplaceUtils';

// ---------------------------------------------------------------------------
// Menu model. ``key`` is the URL under /settings (section or
// section/sub). Groups drive the rail's uppercase headers and the
// middle breadcrumb.
// ---------------------------------------------------------------------------

const GROUPS = [
  {
    key: 'account',
    label: 'Account',
    breadcrumb: null, // account items read "Settings › <Section>"
    items: [
      { key: 'profile',       label: 'Profile',       Icon: PersonIcon },
      { key: 'security',      label: 'Security',      Icon: GppGoodIcon },
      { key: 'preferences',   label: 'Preferences',   Icon: TuneIcon },
      { key: 'notifications', label: 'Notifications', Icon: NotificationsIcon },
      { key: 'api-keys',      label: 'API Keys',      Icon: KeyIcon },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    breadcrumb: { text: 'Billing', link: '/settings/billing' },
    items: [
      { key: 'billing',          label: 'Overview',       Icon: DashboardIcon },
      { key: 'billing/usage',    label: 'Usage & Limits', Icon: ShowChartIcon },
      { key: 'billing/activity', label: 'Activity',       Icon: HistoryIcon },
      { key: 'billing/invoices', label: 'Invoices',       Icon: ReceiptIcon },
    ],
  },
  {
    key: 'monetization',
    label: 'Monetization',
    breadcrumb: { text: 'Monetization', link: '/settings/monetization' },
    items: [
      { key: 'monetization',           label: 'Overview',          Icon: DashboardIcon },
      { key: 'monetization/published', label: 'Published Signals', Icon: StorefrontIcon },
      { key: 'monetization/payouts',   label: 'Payouts',           Icon: PaidIcon },
    ],
  },
  {
    key: 'organization',
    label: 'Organization',
    breadcrumb: null,
    items: [
      { key: 'organization', label: 'Organization',         Icon: CorporateFareIcon, orgOnly: true },
      // Org-level roster: invite people, set roles, assign workspaces.
      // Per-workspace grants moved to each workspace's detail page.
      { key: 'members',      label: 'Organization Members', Icon: GroupIcon },
    ],
  },
];

// Legacy /settings/<tab> keys from the previous shell.
const KEY_ALIASES = { 'api-tokens': 'api-keys' };

// Legacy full keys: the old Rate Card page is now a dialog on
// Usage & Limits, so deep links land there. Cashout and Earnings were
// folded into the Payouts tab — old links land there.
const FULL_KEY_ALIASES = {
  'billing/rates':         'billing/usage',
  'monetization/cashout':  'monetization/payouts',
  'monetization/earnings': 'monetization/payouts',
};

// Billing/monetization default sub-keys ('' segment = first item).
const keyFor = (section, sub) => {
  const base = KEY_ALIASES[section] || section;
  const full = sub ? `${base}/${sub}` : base;
  return FULL_KEY_ALIASES[full] || full;
};

// ---------------------------------------------------------------------------
// The grouped rail — same anatomy as GoalSideNav, plus overline group
// headers and dividers between groups.
// ---------------------------------------------------------------------------

const SettingsRail = ({ groups, active }) => (
  <Box
    sx={{
      width: { xs: '100%', md: 200 },
      flexShrink: 0,
      position: { md: 'sticky' },
      top: { md: 16 },
      alignSelf: 'flex-start',
    }}
  >
    {groups.map((group, gi) => (
      <Box key={group.key}>
        {gi > 0 && <Divider sx={{ my: 1.25 }} />}
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            px: 1,
            mb: 0.25,
            fontSize: 10,
            letterSpacing: 1,
            fontWeight: 600,
            color: 'text.secondary',
          }}
        >
          {group.label}
        </Typography>
        <List disablePadding>
          {group.items.map(({ key, label, Icon }) => {
            const selected = active === key;
            return (
              <ListItemButton
                key={key}
                component={RouterLink}
                to={`/settings/${key}`}
                selected={selected}
                disableRipple
                sx={{
                  position: 'relative',
                  px: 1,
                  py: '3px',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '60%',
                    width: '4px',
                    backgroundColor: 'transparent',
                    transition: 'background-color 120ms ease',
                  },
                  '&:hover': { backgroundColor: 'transparent' },
                  '&:hover::after': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.35) },
                  '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'primary.light' },
                  '&.Mui-selected::after': { backgroundColor: 'primary.main' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 30, color: selected ? 'primary.main' : 'text.secondary' }}>
                  <Icon sx={{ fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: 13,
                    fontWeight: selected ? 600 : 300,
                    letterSpacing: '0.01em',
                    color: selected ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    ))}
  </Box>
);

// ---------------------------------------------------------------------------
// The shell
// ---------------------------------------------------------------------------

const SettingsPage = () => {
  const { section: sectionParam, sub: subParam } = useParams();
  const dispatch = useDispatch();

  // Billing rail gate: for ORG-billed accounts only the org's
  // owner / admins / billing managers see the Billing group. The
  // balance response carries the billing owner (scope + uid); the
  // caller's role per org comes from /settings/public. Defaults to
  // visible until both have loaded (no flicker for the common case).
  const billingBalance = useSelector((s) => s.billing_balance?.response);
  const viewerOrgs = useViewerOrganizations();
  useEffect(() => { dispatch(fetchBillingBalance()); }, [dispatch]);
  const canSeeBilling = useMemo(() => {
    if (billingBalance?.scope !== 'org') return true;
    const role = viewerOrgs.find((o) => o.uid === billingBalance.uid)?.role;
    return ['owner', 'admin', 'billing'].includes(role);
  }, [billingBalance, viewerOrgs]);

  // The Organization item is only shown for users who belong to a
  // Team+ (organization-tier) plan. ``/organization/mine`` is the gate.
  // Re-checked whenever the billing owner changes (an org-tier upgrade
  // provisions the org and flips the balance scope user -> org), so
  // the Organization item appears without a full page reload.
  const orgMineSlice = useSelector((s) => s.settings_organization_mine) || {};
  useEffect(() => {
    dispatch(fetchOrganizationMine());
  }, [billingBalance?.scope, billingBalance?.uid, dispatch]);
  const hasOrg = orgMineSlice.error
    ? false
    : Boolean(orgMineSlice.response?.can_invite_members);

  const groups = useMemo(
    () => GROUPS
      .filter((g) => g.key !== 'billing' || canSeeBilling)
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => !it.orgOnly || hasOrg),
      }))
      .filter((g) => g.items.length > 0),
    [hasOrg, canSeeBilling],
  );

  const requestedKey = keyFor(sectionParam || 'profile', subParam);
  const active = useMemo(() => {
    for (const g of groups) {
      const item = g.items.find((it) => it.key === requestedKey);
      if (item) return { group: g, item };
    }
    return { group: groups[0], item: groups[0].items[0] };
  }, [groups, requestedKey]);

  const { group, item } = active;

  // The billing/monetization sub segment for the active item
  // ('billing/usage' -> 'usage'; 'billing' -> section default).
  const activeSub = item.key.includes('/') ? item.key.split('/')[1] : null;

  const renderPanel = () => {
    switch (item.key) {
      case 'profile':       return <ProfileTabPlaceholder />;
      case 'security':      return <SecurityTab />;
      case 'preferences':   return <PreferencesTab />;
      case 'notifications': return <NotificationsTab />;
      case 'api-keys':      return <APITokensPage embedded />;
      case 'organization':  return <OrganizationSettingsTab />;
      case 'members':       return <OrganizationMembersTab />;
      default:
        if (item.key.startsWith('billing')) {
          return <BillingSection sub={activeSub || 'overview'} />;
        }
        if (item.key.startsWith('monetization')) {
          return <MonetizationPage embedded section={activeSub || 'dashboard'} />;
        }
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Settings › [Group ›] Section */}
      <PageBreadcrumbs
        items={[
          { text: 'Settings', icon: <SettingsIcon />, link: '/settings/profile' },
          ...(group.breadcrumb && group.breadcrumb.text !== item.label
            ? [{ ...group.breadcrumb }]
            : []),
          { text: item.label },
        ]}
        mb={2}
      />

      {/* Two-column body — identical structure to GoalDetailPage:
          borderless grouped rail, one outlined content panel. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
        <SettingsRail groups={groups} active={item.key} />

        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {renderPanel()}
        </Paper>
      </Box>
    </Box>
  );
};

export default SettingsPage;
